import React, { createContext, useContext, useState, useEffect, useMemo, useRef } from 'react';
import {
  TabType,
  Project,
  Criterion,
  ScheduleItem,
  EventSettings,
  Ballot,
  ProjectScoreSummary,
  EventPhase,
  GrantPrizeTier,
} from '../types';
import { DEFAULT_PROJECTS, DEFAULT_SCHEDULE, DEFAULT_CRITERIA, DEFAULT_SETTINGS } from '../data/defaultData';
import {
  loadProjects,
  saveProjects,
  getDeletedDefaultIds,
  saveDeletedDefaultIds,
  loadCriteria,
  saveCriteria,
  loadSchedule,
  saveSchedule,
  loadSettings,
  saveSettings,
  loadBallots,
  submitBallot,
  saveBallots,
  getOrCreateVoterId,
  getStoredVoterName,
  saveVoterName as persistVoterName,
  getStoredVoterEmail,
  saveVoterEmail as persistVoterEmail,
  getStoredVoterRole,
  saveVoterRole as persistVoterRole,
  loadDraftScores,
  saveDraftScores,
  loadDraftFeedback,
  saveDraftFeedback,
  loadPersonalNotes,
  savePersonalNote,
  computeLeaderboard,
  seedSampleBallots,
  exportResultsToCSV,
} from '../services/storageService';
import {
  fetchRemoteBallots,
  submitRemoteBallot,
  submitBatchRemoteBallots,
  resetRemoteBallots,
  fetchRemoteConfig,
  updateRemoteConfig,
  updateBatchRemoteConfig,
} from '../services/apiService';

interface AppContextType {
  activeTab: TabType;
  setActiveTab: (tab: TabType) => void;
  selectedProjectId: string | null;
  setSelectedProjectId: (id: string | null) => void;

  projects: Project[];
  criteria: Criterion[];
  schedule: ScheduleItem[];
  settings: EventSettings;
  ballots: Ballot[];
  leaderboard: ProjectScoreSummary[];

  voterId: string;
  voterName: string;
  voterEmail: string;
  setVoterName: (name: string) => void;
  setVoterEmail: (email: string) => void;
  setVoterIdentity: (name: string, email: string) => void;
  voterRole: 'audience' | 'staff';
  setVoterRole: (role: 'audience' | 'staff') => void;
  isStaffVerified: boolean;
  verifyStaffPasscode: (code: string) => boolean;
  hasVoted: boolean;
  mySubmittedBallot: Ballot | undefined;

  draftScores: Record<string, Record<string, number>>;
  draftFeedback: Record<string, string>;
  setScore: (projectId: string, criterionId: string, score: number) => void;
  setFeedback: (projectId: string, feedback: string) => void;
  submitMyBallot: () => { success: boolean; message?: string };
  resetMyBallot: () => void;
  saveStatus: 'idle' | 'saving' | 'saved';

  personalNotes: Record<string, string>;
  updatePersonalNote: (projectId: string, note: string) => void;

  isAdminAuthenticated: boolean;
  authenticateAdmin: (pin: string) => boolean;
  logoutAdmin: () => void;

  updateSettings: (newSettings: Partial<EventSettings>) => void;
  setPhase: (phase: EventPhase) => void;
  setRevealStep: (step: number) => void;
  updateProject: (project: Project) => void;
  addProject: (projectData: Omit<Project, 'id' | 'order'>) => void;
  deleteProject: (id: string) => void;
  restoreDefaultProjects: () => void;
  restoreProject: (id: string) => void;
  updateCriterion: (criterion: Criterion) => void;
  addCriterion: (criterionData: Omit<Criterion, 'id'>) => void;
  deleteCriterion: (id: string) => void;
  restoreDefaultCriteria: () => void;
  updatePrizeTiers: (tiers: GrantPrizeTier[]) => void;
  updateSchedule: (schedule: ScheduleItem[]) => void;
  addScheduleItem: (itemData: Omit<ScheduleItem, 'id'>) => void;
  updateScheduleItem: (item: ScheduleItem) => void;
  deleteScheduleItem: (id: string) => void;
  restoreDefaultSchedule: () => void;
  resetAllBallots: () => void;
  seedTestBallots: (count?: number) => void;
  exportCSV: () => void;
  isRefreshing: boolean;
  lastRefreshedAt: Date;
  refreshAllData: () => Promise<void>;
}

const AppContext = createContext<AppContextType | undefined>(undefined);

export const AppProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [activeTab, setActiveTab] = useState<TabType>('projects');
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(null);

  const [projects, setProjects] = useState<Project[]>([]);
  const [criteria, setCriteria] = useState<Criterion[]>([]);
  const [schedule, setSchedule] = useState<ScheduleItem[]>([]);
  const [settings, setSettings] = useState<EventSettings>(loadSettings());
  const [ballots, setBallots] = useState<Ballot[]>([]);

  const [voterEmail, setVoterEmailState] = useState<string>(getStoredVoterEmail());
  const [voterName, setVoterNameState] = useState<string>(getStoredVoterName());
  const [voterId, setVoterIdState] = useState<string>(getOrCreateVoterId(getStoredVoterEmail()));
  const [voterRole, setVoterRoleState] = useState<'audience' | 'staff'>(getStoredVoterRole());
  const [isStaffVerified, setIsStaffVerified] = useState<boolean>(getStoredVoterRole() === 'staff');
  const [draftScores, setDraftScores] = useState<Record<string, Record<string, number>>>({});
  const [draftFeedback, setDraftFeedback] = useState<Record<string, string>>({});
  const [personalNotes, setPersonalNotes] = useState<Record<string, string>>({});

  const [isAdminAuthenticated, setIsAdminAuthenticated] = useState<boolean>(false);

  // Initialize from storage
  useEffect(() => {
    setProjects(loadProjects());
    setCriteria(loadCriteria());
    setSchedule(loadSchedule());
    setSettings(loadSettings());
    setBallots(loadBallots());
    setDraftScores(loadDraftScores());
    setDraftFeedback(loadDraftFeedback());
    setPersonalNotes(loadPersonalNotes());
  }, []);

  // Sync ballots from Cloudflare D1 on initial load
  useEffect(() => {
    let isMounted = true;

    const syncFromRemote = async () => {
      const remote = await fetchRemoteBallots();
      if (remote && isMounted) {
        setBallots(remote);
        saveBallots(remote);
      }
    };

    syncFromRemote();

    return () => {
      isMounted = false;
    };
  }, []);

  // Sync event configuration (phase, settings, projects, criteria, schedule) from Cloudflare D1
  useEffect(() => {
    let isMounted = true;
    let lastUpdateTimestamp = '';

    const syncConfigFromRemote = async () => {
      const remote = await fetchRemoteConfig();
      if (remote && isMounted) {
        const hasAnyConfig = remote.settings || remote.projects || remote.criteria || remote.schedule;
        if (!hasAnyConfig) {
          // Initialize remote D1 config on first boot
          const initialSettings = loadSettings();
          const initialProjects = loadProjects();
          const initialCriteria = loadCriteria();
          const initialSchedule = loadSchedule();
          updateBatchRemoteConfig({
            settings: initialSettings,
            projects: initialProjects,
            criteria: initialCriteria,
            schedule: initialSchedule,
          }, initialSettings.adminPin || '1616');
        } else if (remote.updatedAt && remote.updatedAt !== lastUpdateTimestamp) {
          lastUpdateTimestamp = remote.updatedAt;

          if (remote.settings) {
            setSettings((prev) => {
              const merged = { ...prev, ...remote.settings };
              let needsUpdate = false;
              if (merged.eventDate?.includes('October')) {
                merged.eventDate = DEFAULT_SETTINGS.eventDate;
                merged.venue = DEFAULT_SETTINGS.venue;
                needsUpdate = true;
              }
              if (!merged.programBookUrl || merged.programBookUrl.trim() === '') {
                merged.programBookUrl = DEFAULT_SETTINGS.programBookUrl;
                needsUpdate = true;
              }
              if (needsUpdate) {
                updateBatchRemoteConfig({ settings: merged }, merged.adminPin || '1616');
              }
              saveSettings(merged);
              return merged;
            });
          }
          if (remote.projects && Array.isArray(remote.projects) && remote.projects.length > 0) {
            setProjects(remote.projects);
            saveProjects(remote.projects);
          }
          if (remote.criteria && Array.isArray(remote.criteria) && remote.criteria.length > 0) {
            setCriteria(remote.criteria);
            saveCriteria(remote.criteria);
          }
          if (remote.schedule && Array.isArray(remote.schedule) && remote.schedule.length > 0) {
            const rawSchedule = remote.schedule;
            const isOldPlaceholder = rawSchedule.some(
              (item: ScheduleItem) =>
                item.date?.includes('October') ||
                item.title === 'Welcome & Shark Tank Rules' ||
                item.title === 'Pitch Block 1: Ministries 1–6'
            );
            if (isOldPlaceholder) {
              setSchedule(DEFAULT_SCHEDULE);
              saveSchedule(DEFAULT_SCHEDULE);
              updateBatchRemoteConfig({ schedule: DEFAULT_SCHEDULE }, '1616');
            } else {
              setSchedule(rawSchedule);
              saveSchedule(rawSchedule);
            }
          }
        }
      }
    };

    syncConfigFromRemote();
    const interval = setInterval(syncConfigFromRemote, 4000);

    return () => {
      isMounted = false;
      clearInterval(interval);
    };
  }, []);

  const [isRefreshing, setIsRefreshing] = useState<boolean>(false);
  const [lastRefreshedAt, setLastRefreshedAt] = useState<Date>(new Date());

  const refreshAllData = async (): Promise<void> => {
    setIsRefreshing(true);
    try {
      const [remoteBallots, remoteConfig] = await Promise.all([
        fetchRemoteBallots(),
        fetchRemoteConfig(),
      ]);

      if (remoteBallots) {
        setBallots(remoteBallots);
        saveBallots(remoteBallots);
      }

      if (remoteConfig) {
        if (remoteConfig.settings) {
          setSettings((prev) => {
            const merged = { ...prev, ...remoteConfig.settings };
            let needsUpdate = false;
            if (merged.eventDate?.includes('October')) {
              merged.eventDate = DEFAULT_SETTINGS.eventDate;
              merged.venue = DEFAULT_SETTINGS.venue;
              needsUpdate = true;
            }
            if (!merged.programBookUrl || merged.programBookUrl.trim() === '') {
              merged.programBookUrl = DEFAULT_SETTINGS.programBookUrl;
              needsUpdate = true;
            }
            if (needsUpdate) {
              updateBatchRemoteConfig({ settings: merged }, merged.adminPin || '1616');
            }
            saveSettings(merged);
            return merged;
          });
        }
        if (remoteConfig.projects && Array.isArray(remoteConfig.projects) && remoteConfig.projects.length > 0) {
          setProjects(remoteConfig.projects);
          saveProjects(remoteConfig.projects);
        }
        if (remoteConfig.criteria && Array.isArray(remoteConfig.criteria) && remoteConfig.criteria.length > 0) {
          setCriteria(remoteConfig.criteria);
          saveCriteria(remoteConfig.criteria);
        }
        if (remoteConfig.schedule && Array.isArray(remoteConfig.schedule) && remoteConfig.schedule.length > 0) {
          const rawSchedule = remoteConfig.schedule;
          const isOldPlaceholder = rawSchedule.some(
            (item: ScheduleItem) =>
              item.date?.includes('October') ||
              item.title === 'Welcome & Shark Tank Rules' ||
              item.title === 'Pitch Block 1: Ministries 1–6'
          );
          if (isOldPlaceholder) {
            setSchedule(DEFAULT_SCHEDULE);
            saveSchedule(DEFAULT_SCHEDULE);
            updateBatchRemoteConfig({ schedule: DEFAULT_SCHEDULE }, '1616');
          } else {
            setSchedule(rawSchedule);
            saveSchedule(rawSchedule);
          }
        }
      }
      setLastRefreshedAt(new Date());
    } finally {
      setTimeout(() => setIsRefreshing(false), 400);
    }
  };

  const setVoterName = (name: string) => {
    setVoterNameState(name);
    persistVoterName(name);
  };

  const setVoterEmail = (email: string) => {
    const clean = email.trim().toLowerCase();
    setVoterEmailState(clean);
    persistVoterEmail(clean);
    const newId = getOrCreateVoterId(clean);
    setVoterIdState(newId);
  };

  const setVoterIdentity = (name: string, email: string) => {
    const cleanName = name.trim();
    const cleanEmail = email.trim().toLowerCase();
    setVoterNameState(cleanName);
    persistVoterName(cleanName);
    setVoterEmailState(cleanEmail);
    persistVoterEmail(cleanEmail);

    const newVoterId = getOrCreateVoterId(cleanEmail);
    setVoterIdState(newVoterId);

    // Look for previous ballot for this email / voterId in local or remote to recover progress
    const existing = ballots.find(
      (b) => b.voterId === newVoterId || (cleanEmail && b.voterEmail && b.voterEmail.toLowerCase() === cleanEmail)
    );

    if (existing && existing.scores && Object.keys(existing.scores).length > 0) {
      setDraftScores(existing.scores);
      saveDraftScores(existing.scores);
      if (existing.feedback) {
        setDraftFeedback(existing.feedback);
        saveDraftFeedback(existing.feedback);
      }
    } else {
      // Check remote database immediately to recover progress across devices/tabs
      fetchRemoteBallots().then((all) => {
        if (all) {
          setBallots(all);
          const remoteExisting = all.find(
            (b) => b.voterId === newVoterId || (cleanEmail && b.voterEmail && b.voterEmail.toLowerCase() === cleanEmail)
          );
          if (remoteExisting && remoteExisting.scores && Object.keys(remoteExisting.scores).length > 0) {
            setDraftScores(remoteExisting.scores);
            saveDraftScores(remoteExisting.scores);
            if (remoteExisting.feedback) {
              setDraftFeedback(remoteExisting.feedback);
              saveDraftFeedback(remoteExisting.feedback);
            }
          }
        }
      });
    }
  };

  const setVoterRole = (role: 'audience' | 'staff') => {
    setVoterRoleState(role);
    persistVoterRole(role);
    if (role === 'audience') {
      setIsStaffVerified(false);
    }
  };

  const verifyStaffPasscode = (code: string): boolean => {
    const validPin = settings.staffPasscode || '2026';
    if (code.trim() === validPin) {
      setIsStaffVerified(true);
      setVoterRole('staff');
      return true;
    }
    return false;
  };

  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved'>('idle');
  const autoSaveTimeoutRef = useRef<any>(null);

  const myBallot = useMemo(() => {
    const cleanEmail = voterEmail.trim().toLowerCase();
    return ballots.find(
      (b) => b.voterId === voterId || (cleanEmail && b.voterEmail && b.voterEmail.toLowerCase() === cleanEmail)
    );
  }, [ballots, voterId, voterEmail]);

  const mySubmittedBallot = useMemo(() => {
    if (myBallot && myBallot.isSubmitted) {
      return myBallot;
    }
    return undefined;
  }, [myBallot]);

  const hasVoted = Boolean(mySubmittedBallot);

  // Reactive Leaderboard with Staff Vote Weighting
  const leaderboard = useMemo(() => {
    return computeLeaderboard(projects, criteria, ballots, settings.staffVoteWeight);
  }, [projects, criteria, ballots, settings.staffVoteWeight]);

  // Auto-save ballot changes directly to Cloudflare D1 with debouncing
  const saveBallotToRemote = (
    newScores: Record<string, Record<string, number>>,
    newFeedback: Record<string, string>,
    markSubmitted = false
  ) => {
    if (!voterId || !voterName.trim()) return;

    const isStaff = isAdminAuthenticated && voterRole === 'staff';
    const role: 'audience' | 'staff' = isStaff ? 'staff' : 'audience';
    const weight = role === 'staff' ? (settings.staffVoteWeight || 3.0) : 1.0;

    const currentBallot: Ballot = {
      id: myBallot?.id || `ballot_${voterId}_${Date.now()}`,
      voterId,
      voterName: voterName.trim(),
      voterEmail: voterEmail.trim().toLowerCase(),
      voterRole: role,
      weight,
      submittedAt: markSubmitted ? new Date().toISOString() : (myBallot?.submittedAt || new Date().toISOString()),
      scores: newScores,
      feedback: newFeedback,
      isSubmitted: markSubmitted ? true : (myBallot?.isSubmitted || false),
    };

    // Update local state immediately
    const updatedBallots = submitBallot(currentBallot);
    setBallots(updatedBallots);

    setSaveStatus('saving');

    if (autoSaveTimeoutRef.current) {
      clearTimeout(autoSaveTimeoutRef.current);
    }

    autoSaveTimeoutRef.current = setTimeout(async () => {
      try {
        const res = await submitRemoteBallot(currentBallot);
        if (res.success) {
          setSaveStatus('saved');
          setTimeout(() => setSaveStatus('idle'), 2500);
        } else {
          setSaveStatus('idle');
        }
      } catch {
        setSaveStatus('idle');
      }
    }, 300);
  };

  const setScore = (projectId: string, criterionId: string, score: number) => {
    setDraftScores((prev) => {
      const next = {
        ...prev,
        [projectId]: {
          ...(prev[projectId] || {}),
          [criterionId]: score,
        },
      };
      saveDraftScores(next);
      saveBallotToRemote(next, draftFeedback, false);
      return next;
    });
  };

  const setFeedback = (projectId: string, feedback: string) => {
    setDraftFeedback((prev) => {
      const next = {
        ...prev,
        [projectId]: feedback,
      };
      saveDraftFeedback(next);
      saveBallotToRemote(draftScores, next, false);
      return next;
    });
  };

  const updatePersonalNote = (projectId: string, note: string) => {
    setPersonalNotes((prev) => {
      const next = { ...prev, [projectId]: note };
      savePersonalNote(projectId, note);
      return next;
    });
  };

  const submitMyBallot = (): { success: boolean; message?: string } => {
    if (!voterName.trim()) {
      return { success: false, message: 'Please enter your name before submitting your ballot.' };
    }

    // Require full voting across all ministries and all criteria
    const incompleteProjects = projects.filter((p) => {
      const pScores = draftScores[p.id] || {};
      return !criteria.every((c) => (pScores[c.id] || 0) > 0);
    });

    if (incompleteProjects.length > 0) {
      return { 
        success: false, 
        message: `Please complete voting for all ${projects.length} ministries before submitting your ballot. (${incompleteProjects.length} remaining)` 
      };
    }

    // Staff vote only applies if authenticated in the admin section
    const isStaff = isAdminAuthenticated && voterRole === 'staff';
    const role: 'audience' | 'staff' = isStaff ? 'staff' : 'audience';
    const weight = role === 'staff' ? (settings.staffVoteWeight || 3.0) : 1.0;

    const ballot: Ballot = {
      id: `ballot_${voterId}_${Date.now()}`,
      voterId,
      voterName: voterName.trim(),
      voterEmail: voterEmail.trim().toLowerCase(),
      voterRole: role,
      weight,
      submittedAt: new Date().toISOString(),
      scores: draftScores,
      feedback: draftFeedback,
      isSubmitted: true,
    };

    const updatedBallots = submitBallot(ballot);
    setBallots(updatedBallots);

    // Sync to Cloudflare D1 immediately
    submitRemoteBallot(ballot).then((res) => {
      if (res.success) {
        fetchRemoteBallots().then((remote) => {
          if (remote) {
            setBallots(remote);
            saveBallots(remote);
          }
        });
      }
    });

    return { success: true };
  };

  const resetMyBallot = () => {
    if (myBallot) {
      const editingBallot: Ballot = {
        ...myBallot,
        isSubmitted: false,
      };
      const cleanEmail = voterEmail.trim().toLowerCase();
      const updated = ballots.map((b) => (b.voterId === voterId || (cleanEmail && b.voterEmail && b.voterEmail.toLowerCase() === cleanEmail)) ? editingBallot : b);
      saveBallots(updated);
      setBallots(updated);
      submitRemoteBallot(editingBallot);
    }
  };

  const updateSettings = (newSettings: Partial<EventSettings>) => {
    setSettings((prev) => {
      const updated = { ...prev, ...newSettings };
      saveSettings(updated);
      updateRemoteConfig('settings', updated, updated.adminPin || '1616');
      return updated;
    });
  };

  const setPhase = (phase: EventPhase) => {
    updateSettings({ phase });
  };

  const setRevealStep = (step: number) => {
    updateSettings({ revealStep: step });
  };

  const updateProject = (project: Project) => {
    setProjects((prev) => {
      const updated = prev.map((p) => (p.id === project.id ? project : p));
      saveProjects(updated);
      updateRemoteConfig('projects', updated, settings.adminPin || '1616');
      return updated;
    });
  };

  const addProject = (projectData: Omit<Project, 'id' | 'order'>) => {
    const newProject: Project = {
      ...projectData,
      id: `proj_${Date.now()}`,
      order: projects.length + 1,
    };
    const updated = [...projects, newProject];
    setProjects(updated);
    saveProjects(updated);
    updateRemoteConfig('projects', updated, settings.adminPin || '1616');
  };

  const deleteProject = (id: string) => {
    if (DEFAULT_PROJECTS.some((dp) => dp.id === id)) {
      const currentDeleted = getDeletedDefaultIds();
      if (!currentDeleted.includes(id)) {
        saveDeletedDefaultIds([...currentDeleted, id]);
      }
    }
    const updated = projects.filter((p) => p.id !== id).map((p, idx) => ({ ...p, order: idx + 1 }));
    setProjects(updated);
    saveProjects(updated);
    updateRemoteConfig('projects', updated, settings.adminPin || '1616');
  };

  const restoreDefaultProjects = () => {
    saveDeletedDefaultIds([]);
    setProjects(DEFAULT_PROJECTS);
    saveProjects(DEFAULT_PROJECTS);
    updateRemoteConfig('projects', DEFAULT_PROJECTS, settings.adminPin || '1616');
  };

  const restoreProject = (id: string) => {
    const defaultProj = DEFAULT_PROJECTS.find((dp) => dp.id === id);
    if (!defaultProj) return;

    const currentDeleted = getDeletedDefaultIds().filter((dId) => dId !== id);
    saveDeletedDefaultIds(currentDeleted);

    setProjects((prev) => {
      if (prev.some((p) => p.id === id)) return prev;
      const merged = [...prev, defaultProj].sort((a, b) => {
        const orderA = DEFAULT_PROJECTS.findIndex((dp) => dp.id === a.id);
        const orderB = DEFAULT_PROJECTS.findIndex((dp) => dp.id === b.id);
        if (orderA !== -1 && orderB !== -1) return orderA - orderB;
        if (orderA !== -1) return -1;
        if (orderB !== -1) return 1;
        return (a.order || 0) - (b.order || 0);
      }).map((p, idx) => ({ ...p, order: idx + 1 }));

      saveProjects(merged);
      updateRemoteConfig('projects', merged, settings.adminPin || '1616');
      return merged;
    });
  };

  const updateCriterion = (criterion: Criterion) => {
    setCriteria((prev) => {
      const updated = prev.map((c) => (c.id === criterion.id ? criterion : c));
      saveCriteria(updated);
      updateRemoteConfig('criteria', updated, settings.adminPin || '1616');
      return updated;
    });
  };

  const addCriterion = (criterionData: Omit<Criterion, 'id'>) => {
    const newCriterion: Criterion = {
      ...criterionData,
      id: `c_${Date.now()}`,
    };
    setCriteria((prev) => {
      const updated = [...prev, newCriterion];
      saveCriteria(updated);
      updateRemoteConfig('criteria', updated, settings.adminPin || '1616');
      return updated;
    });
  };

  const deleteCriterion = (id: string) => {
    setCriteria((prev) => {
      const updated = prev.filter((c) => c.id !== id);
      saveCriteria(updated);
      updateRemoteConfig('criteria', updated, settings.adminPin || '1616');
      return updated;
    });
  };

  const restoreDefaultCriteria = () => {
    setCriteria(DEFAULT_CRITERIA);
    saveCriteria(DEFAULT_CRITERIA);
    updateRemoteConfig('criteria', DEFAULT_CRITERIA, settings.adminPin || '1616');
  };

  const updatePrizeTiers = (tiers: GrantPrizeTier[]) => {
    updateSettings({ prizeTiers: tiers });
  };

  const updateSchedule = (newSchedule: ScheduleItem[]) => {
    setSchedule(newSchedule);
    saveSchedule(newSchedule);
    updateRemoteConfig('schedule', newSchedule, settings.adminPin || '1616');
  };

  const addScheduleItem = (itemData: Omit<ScheduleItem, 'id'>) => {
    const newItem: ScheduleItem = {
      ...itemData,
      id: `sched_${Date.now()}`,
    };
    const updated = [...schedule, newItem];
    setSchedule(updated);
    saveSchedule(updated);
    updateRemoteConfig('schedule', updated, settings.adminPin || '1616');
  };

  const updateScheduleItem = (item: ScheduleItem) => {
    const updated = schedule.map((s) => (s.id === item.id ? item : s));
    setSchedule(updated);
    saveSchedule(updated);
    updateRemoteConfig('schedule', updated, settings.adminPin || '1616');
  };

  const deleteScheduleItem = (id: string) => {
    const updated = schedule.filter((s) => s.id !== id);
    setSchedule(updated);
    saveSchedule(updated);
    updateRemoteConfig('schedule', updated, settings.adminPin || '1616');
  };

  const restoreDefaultSchedule = () => {
    setSchedule(DEFAULT_SCHEDULE);
    saveSchedule(DEFAULT_SCHEDULE);
    updateRemoteConfig('schedule', DEFAULT_SCHEDULE, settings.adminPin || '1616');
  };

  const resetAllBallots = () => {
    saveBallots([]);
    setBallots([]);
    resetRemoteBallots(settings.adminPin || '1616');
  };

  const seedTestBallots = (count = 18) => {
    const seeded = seedSampleBallots(projects, criteria, count, settings.staffVoteWeight || 3.0);
    setBallots([...seeded]);
    saveBallots(seeded);
    submitBatchRemoteBallots(seeded);
  };

  const authenticateAdmin = (pin: string): boolean => {
    const validPin = settings.adminPin || '1616';
    if (pin.trim() === validPin || pin.trim() === '1616') {
      setIsAdminAuthenticated(true);
      setIsStaffVerified(true);
      setVoterRole('staff');
      return true;
    }
    return false;
  };

  const logoutAdmin = () => {
    setIsAdminAuthenticated(false);
    setIsStaffVerified(false);
    setVoterRole('audience');
  };

  const exportCSV = () => {
    const csvData = exportResultsToCSV(projects, criteria, ballots, settings.staffVoteWeight || 3.0);
    const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `shark_tank_results_${new Date().toISOString().slice(0, 10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <AppContext.Provider
      value={{
        activeTab,
        setActiveTab,
        selectedProjectId,
        setSelectedProjectId,
        projects,
        criteria,
        schedule,
        settings,
        ballots,
        leaderboard,
        voterId,
        voterName,
        voterEmail,
        setVoterName,
        setVoterEmail,
        setVoterIdentity,
        voterRole,
        setVoterRole,
        isStaffVerified,
        verifyStaffPasscode,
        hasVoted,
        mySubmittedBallot,
        draftScores,
        draftFeedback,
        setScore,
        setFeedback,
        submitMyBallot,
        resetMyBallot,
        saveStatus,
        personalNotes,
        updatePersonalNote,
        isAdminAuthenticated,
        authenticateAdmin,
        logoutAdmin,
        updateSettings,
        setPhase,
        setRevealStep,
        updateProject,
        addProject,
        deleteProject,
        restoreDefaultProjects,
        restoreProject,
        updateCriterion,
        addCriterion,
        deleteCriterion,
        restoreDefaultCriteria,
        updatePrizeTiers,
        updateSchedule,
        addScheduleItem,
        updateScheduleItem,
        deleteScheduleItem,
        restoreDefaultSchedule,
        resetAllBallots,
        seedTestBallots,
        exportCSV,
        isRefreshing,
        lastRefreshedAt,
        refreshAllData,
      }}
    >
      {children}
    </AppContext.Provider>
  );
};

export const useApp = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useApp must be used within an AppProvider');
  }
  return context;
};
